"use client";

import { useState, type SubmitEvent } from "react";
import { Field } from "./field";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { Button } from "./button";
import "./support-contact-form.css";

type Status = "idle" | "sending" | "sent" | "error";

/**
 * Signed-out fallback for the support page's "Contact us" section (FAIL-442).
 * The raw support email is only rendered for signed-in visitors (see
 * `app/(legal)/support/page.tsx`); everyone else gets this form instead, so
 * the address never appears in page HTML for a bot to scrape.
 *
 * `company` is a honeypot: visually hidden (not `display:none`, which some
 * bots skip) and out of both the tab order and the accessibility tree, so a
 * real visitor — sighted or on a screen reader — never touches it.
 */
export function SupportContactForm() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  async function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch("/api/support-contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, message, company }),
      });
      if (!res.ok) throw new Error("request failed");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }

  if (status === "sent") {
    return <p className="pz-supportform__done">Thanks — we got your message and will reply by email.</p>;
  }

  return (
    <form className="pz-supportform" onSubmit={(e) => void handleSubmit(e)}>
      <div className="pz-supportform__honeypot" aria-hidden="true">
        <label htmlFor="support-company">Company</label>
        <input
          id="support-company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>
      <Field label="Your email" htmlFor="support-email" required>
        <Input
          id="support-email"
          name="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <Field label="Message" htmlFor="support-message" required>
        <Textarea
          id="support-message"
          name="message"
          required
          rows={4}
          maxLength={2000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What do you need help with?"
        />
      </Field>
      <Button type="submit" loading={status === "sending"}>
        {status === "sending" ? "Sending…" : "Send"}
      </Button>
      {status === "error" && (
        <p className="pz-supportform__error" role="alert">
          Something went wrong — please try again in a moment.
        </p>
      )}
    </form>
  );
}
